import CircularProgress from '@mui/material/CircularProgress';
import { useTimeAgo, useIntl, Iconify } from '@mfw/components';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ReactQuill from 'react-quill-new';
import Box from '@mui/material/Box';
import React from 'react';

import type { UiNotification } from '../../../schemas/notification.schema';

import { useSetDailyActivityModal } from '../../../store/useDailyActivityModalStore';
import { useSetMembershipCardSetup } from '../../../store/useMembershipCardSetupStore';
import { useSetMembershipTouAcceptance } from '../../../store/useMembershipTouAcceptanceStore';
import { useSetAccountCardsRequest } from '../../../store/useAccountCardsRequestStore';
import { NOTIFICATION_ICON } from '../../../components/NotificationToast/NotificationToast';
import {
  isActionableNotification,
  openNotificationTarget,
} from '../../../utils/openNotificationTarget';
import {
  useInfiniteNotifications,
  useDeleteNotification,
} from '../../../hooks/notifications';
import { useNavigate } from '../../../router/hooks';
import messages from '../messages';
import { onSurfaceMark } from '../../../theme/onSurface';

const NotificationItem = ({
  notification,
}: {
  notification: UiNotification;
}) => {
  const icon = NOTIFICATION_ICON[notification.type] ?? 'mdi:bell-outline';
  const timeAgo = useTimeAgo();
  const openDailyActivityModal = useSetDailyActivityModal();
  const openMembershipCardSetup = useSetMembershipCardSetup();
  const openMembershipTouAcceptance = useSetMembershipTouAcceptance();
  const openAccountCards = useSetAccountCardsRequest();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { mutate: deleteNotification, isPending: isDeleting } =
    useDeleteNotification();
  const openScheduleDay = React.useCallback(
    (date: string) => navigate('/schedule', { search: `date=${date}` }),
    [navigate],
  );
  const isActionable = isActionableNotification(notification.data);
  return (
    <Box
      sx={{
        '&:hover': { bgcolor: 'action.hover' },
        '&:last-child': { borderBottom: 'none' },
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: isActionable ? 'pointer' : 'default',
        transition: 'background 0.15s',
      }}
      onClick={
        isActionable
          ? () =>
              openNotificationTarget(
                notification.data,
                openDailyActivityModal,
                openMembershipCardSetup,
                openMembershipTouAcceptance,
                openAccountCards,
                openScheduleDay,
              )
          : undefined
      }
      data-testid="notification-item"
      alignItems="flex-start"
      position="relative"
      display="flex"
      gap={1.25}
      py={1.25}
      px={2}
    >
      {/* The glyph sits on the `lighter` tint, which is pale on every
          ladder, so it is measured against THAT, not the page: `main` is
          1.5:1 there, `darker` 4.7:1. */}
      <Box
        bgcolor="primary.lighter"
        justifyContent="center"
        color="primary.darker"
        alignItems="center"
        borderRadius="50%"
        flexShrink={0}
        display="flex"
        height={32}
        width={32}
        mt="2px"
      >
        <Iconify size="small" icon={icon} />
      </Box>

      <Box minWidth={0} flex={1}>
        <Box
          sx={{
            '& .ql-container': { border: 'none' },
            '& .ql-editor': {
              fontWeight: 600,
              lineHeight: 1.4,
              padding: 0,
            },
          }}
        >
          <ReactQuill
            modules={{ toolbar: false }}
            value={notification.title}
            theme="snow"
            readOnly
          />
        </Box>
        <Box
          sx={{
            '& .ql-container': { border: 'none' },
            '& .ql-editor': {
              // Room for a real sentence (the old 2-line clamp cut most bodies
              // mid-word), but still a bound: the panel is capped at 420px and
              // scrolls, so an unclamped rich-text body can fill it end to end
              // and push every other notification out of view.
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 6,
              color: 'text.secondary',
              display: '-webkit-box',
              lineHeight: 1.4,
              overflow: 'hidden',
              padding: 0,
            },
            mt: '2px',
          }}
        >
          <ReactQuill
            modules={{ toolbar: false }}
            value={notification.body}
            theme="snow"
            readOnly
          />
        </Box>
        <Typography
          sx={{ color: 'text.secondary', display: 'block', mt: '4px' }}
          variant="caption"
        >
          {timeAgo(notification.createdAt)}
        </Typography>
      </Box>

      {!notification.readAt && (
        <Box
          bgcolor={onSurfaceMark.primary}
          borderRadius="50%"
          flexShrink={0}
          height={8}
          width={8}
          mt="6px"
        />
      )}

      <IconButton
        sx={{
          '&:hover': { color: 'text.primary', opacity: 1 },
          color: 'text.secondary',
          flexShrink: 0,
          mr: '-6px',
          mt: '-4px',
          opacity: 0.6,
        }}
        onClick={(e) => {
          e.stopPropagation();
          deleteNotification(notification.id);
        }}
        aria-label={formatMessage(messages.deleteNotification)}
        data-testid="notification-item-delete"
        disabled={isDeleting}
        size="small"
      >
        <Iconify icon="mdi:close" size="small" />
      </IconButton>
    </Box>
  );
};

export const NotificationsList = () => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteNotifications();
  const sentinelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) {
    return (
      <Box
        justifyContent="center"
        alignItems="center"
        minHeight={140}
        display="flex"
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  const notifications = data?.data ?? [];

  if (!notifications.length) {
    return (
      <Box
        justifyContent="center"
        color="text.secondary"
        flexDirection="column"
        alignItems="center"
        minHeight={140}
        display="flex"
        gap={1}
      >
        <Iconify icon="mdi:bell-off-outline" size="large" />
        <Typography variant="body2">No notifications yet</Typography>
      </Box>
    );
  }

  return (
    <Box flexDirection="column" display="flex">
      {notifications.map((n) => (
        <NotificationItem notification={n} key={n.id} />
      ))}
      <div style={{ height: 1 }} ref={sentinelRef} />
      {isFetchingNextPage && (
        <Box justifyContent="center" display="flex" py={1}>
          <CircularProgress size={20} />
        </Box>
      )}
    </Box>
  );
};
